import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { ButtonComponent, ButtonType, InputOutline } from "@sxm-content-creators/tools-ds";

import { SnackbarService } from "../../components/snackbar/snackbar.service";
import { NavigationService } from "../../shared/services/navigation.service";
import { AuthService } from "../auth.service";
import { passwordRequirementsList, passwordsMatchValidator, passwordStrengthValidator } from "../password-validators";

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrl: "./login.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ReactiveFormsModule, RouterModule, MatButtonModule, MatDividerModule, ButtonComponent, InputOutline]
})
export class LoginComponent implements OnInit {
    private readonly snackbarService = inject(SnackbarService);
    private readonly authService = inject(AuthService);
    private readonly navigationService = inject(NavigationService);
    private readonly router = inject(Router);

    readonly ButtonType = ButtonType;
    readonly isLoading = signal(false);
    readonly isNewPasswordRequired = signal(false);

    ngOnInit(): void {
        if (this.authService.isAuthenticated()) {
            this.router.navigate(["/"]);
        }
    }

    readonly passwordRequirementsList = passwordRequirementsList;

    form = new FormGroup({
        email: new FormControl("", [Validators.required, Validators.email]),
        password: new FormControl("", [Validators.required])
    });

    newPasswordForm = new FormGroup(
        {
            newPassword: new FormControl("", [Validators.required, passwordStrengthValidator()]),
            confirmPassword: new FormControl("", [Validators.required])
        },
        {
            validators: passwordsMatchValidator()
        }
    );

    get isSubmitDisabled(): boolean {
        return this.form.invalid || this.isLoading();
    }

    get isNewPasswordSubmitDisabled(): boolean {
        return this.newPasswordForm.invalid || this.isLoading();
    }

    get newPasswordStarted(): boolean {
        return (this.newPasswordForm.get("newPassword")?.value ?? "").length > 0;
    }

    get newPasswordRequirements(): { label: string; met: boolean }[] {
        const value: string = this.newPasswordForm.get("newPassword")?.value ?? "";
        return this.passwordRequirementsList.map(req => ({ label: req.label, met: req.check(value) }));
    }

    onSubmit(): void {
        if (this.form.invalid) {
            return;
        }

        const { email, password } = this.form.value;

        this.isLoading.set(true);

        this.authService.login(email!, password!).subscribe({
            next: ({ nextStep }) => {
                this.isLoading.set(false);
                if (nextStep.signInStep === "DONE") {
                    this.router.navigate([this.navigationService.postLoginRoute()]);
                } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                    this.isNewPasswordRequired.set(true);
                } else {
                    this.snackbarService.show("Sign in could not be completed. Please try again.", "error");
                }
            },
            error: () => {
                this.isLoading.set(false);
                this.snackbarService.show("Invalid email or password.", "error");
            }
        });
    }

    onSetNewPassword(): void {
        if (this.newPasswordForm.invalid) {
            return;
        }

        const { newPassword } = this.newPasswordForm.value;

        this.isLoading.set(true);

        this.authService.confirmNewPassword(newPassword!).subscribe({
            next: ({ nextStep }) => {
                this.isLoading.set(false);
                if (nextStep.signInStep === "DONE") {
                    this.router.navigate([this.navigationService.postLoginRoute()]);
                } else {
                    this.snackbarService.show("Password update could not be completed. Please try again.", "error");
                }
            },
            error: (err: { message?: string }) => {
                this.isLoading.set(false);
                const message = err?.message || "Could not set new password. Please try again.";
                this.snackbarService.show(message, "error");
            }
        });
    }

    signInWithOkta(): void {
        this.authService.signInWithOkta().catch((err: unknown) => {
            console.error("Okta sign in failed:", err);
            this.snackbarService.show("Sign in with Okta could not be completed. Please try again.", "error");
        });
    }
}
